import moment from 'moment'
import { format } from 'date-fns'
import _ from 'lodash'

import store from '@/store'

function getLocaleForFormatting() {
  const account = store.access.getAccount()
  let language = account.language === 'en' ? 'en-US' : account.language
  language = language?.split('-')[0] ?? 'en'
  try {
    return require(`date-fns/locale/${language}/index.js`)
  } catch {
    return require('date-fns/locale/en-US/index.js')
  }
}

export function sanitizeDate(dateToSanitize) {
  let sanitizedDate = dateToSanitize
  if (
    typeof sanitizedDate === 'string' &&
    sanitizedDate.split('/').length - 1 === 2
  ) {
    let splitedDate = sanitizedDate.split('/')

    if (splitedDate[0] > 12) {
      const year = splitedDate.pop()
      splitedDate = splitedDate.reverse()
      splitedDate.push(year)
      sanitizedDate = splitedDate.join('/')
    }
  }

  if (sanitizedDate instanceof Date === false) {
    sanitizedDate = new Date(sanitizedDate)
  }

  return sanitizedDate
}

/*
  The following maethod is mainly based on the rules specified in this document: https://rever.atlassian.net/wiki/spaces/RP/pages/118030351/Humanizing+Dates+and+Times
  */
export function humanizeDate(unformattedDate = new Date()) {
  const sanitizedDate = this.sanitizeDate(unformattedDate)

  const date = moment(sanitizedDate)
  if (!date.isValid()) return ''

  const now = moment()

  // This covers future dates
  if (now.isAfter(date)) return date.format('ll')

  // This covers the "Just now" case for dates with less than 1 minute of difference
  const secondsFromNow = now.diff(date, 'seconds')
  if (secondsFromNow < 60) {
    return this.$t('common.date_options.just_now')
  }

  // This covers the cases from "1 minute ago" to "7 days ago" and relies in the criteria used by moment.fromNow method https://momentjs.com/docs/#/displaying/fromnow/
  const daysFromNow = now.diff(date, 'days')
  if (daysFromNow < 8) return date.fromNow()

  // If the date is more than 7 days but still in the current year we only show a formatted date omiting the year. E.g. "Sep. 15"
  if (now.isSame(date, 'year')) {
    return date.format('MMMM DD')
  }

  // If the date's year is different from the current year we return the full formatted date "Sep. 15, 1995"
  return date.format('ll')
}

export function getDateRangeText(dates = []) {
  if (_.isEmpty(dates)) return ''
  const startDate = dates[0]
  const endDate = dates[1]
  return `${formatDate(
    getStartOfDay(startDate),
    'MMM dd, yyyy'
  )} - ${formatDate(getEndOfDay(endDate), 'MMM dd, yyyy')}`
}

function getStartOfDay(date) {
  const currentTzOffset = moment().utcOffset()
  return moment(date)
    .startOf('day')
    .utcOffset(currentTzOffset)
    .format()
}

function getEndOfDay(date) {
  const currentTzOffset = moment().utcOffset()
  return moment(date)
    .endOf('day')
    .utcOffset(currentTzOffset)
    .format()
}

export function formatDate(unformattedDate, formatString = 'P') {
  if (!unformattedDate) return ''

  const dateToFormat = sanitizeDate(unformattedDate)

  return format(dateToFormat, formatString, {
    locale: getLocaleForFormatting(),
  })
}

export default {
  sanitizeDate,
  humanizeDate,
  getDateRangeText,
  formatDate,
}
